// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — the tenant's own words for "assignee" and "owner". Pure: no DOM, no storage.
//
// A TODO's people are linked through two relation types the tenant configures and NAMES itself
// (bound in the configurator's Mobile section). On the measured tenant they read `Zugewiesen an` /
// `Besitzer` in German and `Assigned to` / `Owner` in English — so the app labels people with those,
// as the PQFORCE web client does, rather than with words of its own that the tenant never chose.
//
// The names arrive with the composer's options (`todos/options` → `relationNames`), in the app's
// language. Until they do, or where the tenant has none, every label keeps the app's own word: each
// call site passes its translated fallback.
//
// Module state, like the current language in i18n.ts, because the labels are drawn deep inside
// renderers that have no reason to carry the profile around — and, like `t()`, read at render time.
// ─────────────────────────────────────────────────────────────────────────────

export type RelationRole = "assignee" | "owner";

let names: Record<RelationRole, string> = { assignee: "", owner: "" };
/**
 * The same two relation types named from the PERSON's side (`nameBackward`) — the names of the two
 * LISTS a person has: on the measured tenant `To dos` / `Eigene Todos`, `To-Do List` /
 * `My Own To-Dos`. What the app calls "Assigned to me" and "I own".
 */
let listNames: Record<RelationRole, string> = { assignee: "", owner: "" };

const pickNames = (raw: unknown): Record<RelationRole, string> => {
    const pick = (value: unknown): string => (typeof value === "string" ? value.trim() : "");
    const source = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
    return { assignee: pick(source.assignee), owner: pick(source.owner) };
};

/**
 * What the tenant calls the two roles, and the two lists — junk and missing names count as none.
 * Accepts the remembered shape `{ roles, lists }` too, and a bare role map from before the lists.
 */
export function setRelationNames(raw: unknown, lists?: unknown): void {
    const source = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
    if ("roles" in source || "lists" in source) {
        names = pickNames(source.roles);
        listNames = pickNames(source.lists);
        return;
    }
    names = pickNames(raw);
    listNames = pickNames(lists);
}

/** The current names, for remembering them between starts. */
export function relationNames(): { roles: Record<RelationRole, string>; lists: Record<RelationRole, string> } {
    return { roles: { ...names }, lists: { ...listNames } };
}

/** The tenant's name for the LIST of `role` TODOs a person has, else `fallback` — the app's own. */
export function relationListName(role: RelationRole, fallback: string): string {
    return listNames[role] || fallback;
}

/** The tenant's word for `role`, else `fallback` — the app's own, already translated. */
export function relationName(role: RelationRole, fallback: string): string {
    return names[role] || fallback;
}
